import { ReactNode } from 'react';

import { STATUS_PALETTE } from '@/features/backlog/constants';
import { Popover } from './Popover';
import { Check } from './icons';

type TriggerArg = { open: boolean; toggle: () => void; close: () => void };

/**
 * A small swatch-grid colour picker built on {@link Popover} (portaled, so it isn't clipped
 * inside the scrolling kanban board). Picking a swatch fires `onChange` once and closes —
 * the curated {@link STATUS_PALETTE} keeps persistence to a single write per pick (no live
 * dragging of a native colour input to debounce).
 */
export function ColorPicker({
  value,
  onChange,
  trigger,
  align = 'left',
  side = 'bottom',
}: {
  value: string;
  onChange: (color: string) => void;
  trigger: (arg: TriggerArg) => ReactNode;
  align?: 'left' | 'right';
  side?: 'bottom' | 'right';
}) {
  return (
    <Popover portal align={align} side={side} panelClassName="min-w-0" trigger={trigger}>
      {(close) => (
        <div className="grid grid-cols-6 gap-1.5 p-1">
          {STATUS_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                onChange(c);
                close();
              }}
              className="flex h-7 w-7 items-center justify-center rounded-full ring-1 ring-inset ring-black/25 transition-transform hover:scale-110"
              style={{ background: c }}
              aria-label={`Colour ${c}`}
            >
              {value?.toLowerCase() === c.toLowerCase() ? <Check className="text-black/80" /> : null}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

export default ColorPicker;
